import * as React from 'react';

import { MapType } from './MapType';
import styles from './VillageMap.css';
import { IVillageMapDispatchProps, IVillageMapStoreProps } from './container';
import {
  convertInfrastructureListToFeatureList,
  createVillagePin,
  IInfrastructureObject,
  loadYmapsApi,
} from './helpers';
import { VillageMapInfrastructure } from './infrastructure';
import { RulerControls } from './ruler';
import { ZoomControls } from './zoom';
import { EMapTypes } from '../../constants/map';
import { InfrastructureType } from '../../store/map';
import { trackMapInfrastructureClick } from '../../tracking';
import { trackSwitchMap } from '../../tracking/trackSwitchMap';
import { TIOSMInfrastructure } from '../../types';
import { ApplicationContext } from '../../utils/applicationContext';

type TVillageMapProps = IVillageMapStoreProps & IVillageMapDispatchProps;

export interface IVillageMapState {
  selectedInfrastructures: InfrastructureType[];
  isRuleActive: boolean;
  zoom: number;
  secondInitMap: boolean;
}

const INITIAL_ZOOM = 12;
const YMapsOptions = {
  require: [
    'Map',
    'ObjectManager',
    'templateLayoutFactory',
    'geometry.pixel.Rectangle',
    'shape.Rectangle',
    'control.RulerControl',
    'objectManager.addon.objectsBalloon',
  ],
};
const rullerControlParams = {
  options: {
    scaleLine: false,
    visible: false,
  },
};

export class VillageMap extends React.PureComponent<TVillageMapProps, IVillageMapState> {
  public static contextType = ApplicationContext;
  // @ts-expect-error: TS2612 — declare-поля не поддержаны babel-пресетом jest, а React присваивает context сам
  public context: React.ContextType<typeof ApplicationContext>;
  protected mapContainer: HTMLDivElement | null = null;
  private ymaps: YMaps.IYMaps;
  protected map: YMaps.Map;
  private mapObjectManager: YMaps.ObjectManager;
  private mapInfrastructureObjects: IInfrastructureObject[] = [];

  protected minZoom = 7;
  protected maxZoom = 20;

  public state: IVillageMapState = {
    zoom: INITIAL_ZOOM,
    isRuleActive: false,
    selectedInfrastructures: [],
    secondInitMap: false,
  };

  public componentDidMount() {
    this.initialMap();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: TVillageMapProps) {
    if (nextProps.infrastructureList.length) {
      this.addInfrastructures(nextProps.infrastructureList);
    }
  }

  public render() {
    const { selectedInfrastructures } = this.state;
    const { coordinates, mapType } = this.props;
    const nextViewType = mapType === EMapTypes.satellite ? EMapTypes.map : EMapTypes.satellite;

    return (
      <div className={styles['container']}>
        <div
          ref={ref => {
            this.mapContainer = ref;
          }}
          className={styles['map']}
        />
        <ZoomControls
          onZoomIn={this.onZoom('in')}
          onZoomOut={this.onZoom('out')}
          disabledZoomIn={this.disabledZoomIn()}
          disabledZoomOut={this.disabledZoomOut()}
        />
        <RulerControls onRulerToggle={this.toggleRuler} isSelected={this.state.isRuleActive} />
        <VillageMapInfrastructure selected={selectedInfrastructures} onSelect={this.handleInfrastructureSelect} />
        {this.shouldUseSatelliteMap() && (
          <MapType
            mapType={mapType}
            coordinates={coordinates}
            onChange={() => this.handlerClickMapTypeButton(nextViewType)}
          />
        )}
      </div>
    );
  }

  protected handlerClickMapTypeButton = (mapType: EMapTypes) => {
    this.props.setMapType(mapType);
    this.map.setType(mapType);
    trackSwitchMap(mapType);
  };

  private initialMap = async ({ enterpriseVersionLoad }: { enterpriseVersionLoad?: boolean } = {}) => {
    const { logger } = this.context;
    const { coordinates, yandexApiKeySatellite, mapType, setMapType } = this.props;
    try {
      const loadYmapsOptions =
        this.shouldUseSatelliteMap() && !enterpriseVersionLoad
          ? { satelliteApiKey: yandexApiKeySatellite }
          : { force: enterpriseVersionLoad };

      this.ymaps = await loadYmapsApi(YMapsOptions, loadYmapsOptions);

      if (this.mapContainer) {
        if (this.mapContainer.innerHTML) {
          this.mapContainer.innerHTML = '';
        }

        this.map = new this.ymaps.Map(
          this.mapContainer,
          {
            center: [coordinates.lat, coordinates.lng],
            zoom: INITIAL_ZOOM,
          },
          { suppressMapOpenBlock: true },
        );
        this.map.behaviors.disable('scrollZoom');

        const rulerControl = new this.ymaps.control.RulerControl(rullerControlParams);
        this.mapObjectManager = new this.ymaps.ObjectManager();

        const villagePin = createVillagePin([coordinates.lat, coordinates.lng], this.ymaps);

        this.map.controls.add(rulerControl);
        if (this.shouldUseSatelliteMap() && mapType !== EMapTypes.map) {
          await this.map.setType(mapType);
        }
        this.map.geoObjects.add(this.mapObjectManager);
        this.searchInfrastructures();
        this.map.events.add('actionend', this.searchInfrastructures);
        this.mapObjectManager.add([villagePin]);
        this.mapObjectManager.events.add('click', this.handleInfrastructureObjectClick);
      }
    } catch (error) {
      logger.error(error, { message: 'Error at initialMap', satelliteMapEnabled: this.shouldUseSatelliteMap() });
      if (this.shouldUseSatelliteMap()) {
        if (setMapType) {
          setMapType(EMapTypes.map);
        }
        this.setState({ secondInitMap: true }, () => {
          this.initialMap({ enterpriseVersionLoad: true });
        });
      }
    }
  };

  private searchInfrastructures = () => this.map && this.props.searchInfrastructures(this.map.getBounds());

  protected shouldUseSatelliteMap = () => !this.state.secondInitMap;

  // Показываем попап при клике на пин инфраструктуры на карте
  private handleInfrastructureObjectClick = (event: { get(optionName: string): string }) => {
    const id: string = event.get('objectId');
    const infrastructureObject = this.mapInfrastructureObjects.find(obj => obj.id === id);
    if (infrastructureObject) {
      this.mapObjectManager.objects.balloon.open(id);
      trackMapInfrastructureClick(infrastructureObject.infrastructureType);
    }
  };

  // Переключаем тип инфраструктуры для отображения на карте
  private handleInfrastructureSelect = (type: InfrastructureType) => {
    if (!this.state.selectedInfrastructures.includes(type)) {
      this.setState({
        selectedInfrastructures: [...this.state.selectedInfrastructures, type],
      });
    } else {
      const selectedInfrastructures = this.state.selectedInfrastructures.filter(infr => infr !== type);
      this.setState({
        selectedInfrastructures,
      });
      this.removeInfrastructures([type]);
    }

    if (this.map) {
      this.searchInfrastructures();
      this.map.balloon.close();
    }
  };

  // Добавляем новые пины инфраструктуры на карту. Передаем список инфраструктур
  private addInfrastructures = (infrastructureList: TIOSMInfrastructure[]) => {
    const { selectedInfrastructures } = this.state;

    const featureList = convertInfrastructureListToFeatureList(this.ymaps, infrastructureList);

    // оставляем только инфраструктуры выбранных типов и которых еще нет на карте
    const uniqueFeatureList = featureList
      .filter(obj => selectedInfrastructures.includes(obj.infrastructureType))
      .filter(obj => !this.mapInfrastructureObjects.some(obj1 => obj.id === obj1.id));

    this.mapInfrastructureObjects = this.mapInfrastructureObjects.concat(uniqueFeatureList);

    this.mapObjectManager.add(uniqueFeatureList);
  };

  // Удаляем пины инфраструктуры с карты. Предаем список типов инфраструктуры для удаления
  private removeInfrastructures = (types: InfrastructureType[]) => {
    const objectsForRemove = this.mapInfrastructureObjects.filter(obj => types.includes(obj.infrastructureType));

    this.mapObjectManager.remove(objectsForRemove.map(obj => obj.id));

    this.mapInfrastructureObjects = this.mapInfrastructureObjects.filter(obj => !objectsForRemove.includes(obj));
  };

  protected onZoom = (type: 'in' | 'out') => () => {
    if (this.map) {
      const newZoom = this.map.getZoom() + (type === 'in' ? 1 : -1);
      if (newZoom < this.minZoom || newZoom > this.maxZoom) {
        return;
      }
      this.setState({ zoom: newZoom });
      this.map.setZoom(newZoom);
    }
  };

  private toggleRuler = () => {
    if (this.map) {
      const ruler = this.map.behaviors.get<YMaps.RulerControl>('ruler');

      if (ruler.isEnabled()) {
        ruler.close();
        ruler.disable();
        this.setState({ isRuleActive: false });
      } else {
        ruler.enable();
        this.setState({ isRuleActive: true });
      }
    }
  };

  protected disabledZoomIn = () => this.maxZoom - 1 <= this.state.zoom;
  protected disabledZoomOut = () => this.minZoom >= this.state.zoom;
}
